<template>
	<KkButtonBase
		class="kk-button"
		:class="{
			[`kk-button--${size}`]: size,
			[`kk-button--${size}-has-leading`]: size && leadingIcon,
			[`kk-button--${size}-has-trailing`]: size && trailingIcon,
			[`kk-button--${type}`]: type,
			'kk-button--no-padding': hasNoPadding,
		}"
		:is-full-width="isFullWidth"
		:is-disabled="isDisabled"
		:is-loading="isLoading"
		:is-click-bypass-limit="isClickBypassLimit"
		@click="click"
	>
		<template #leading>
			<KkIcon
				v-if="leadingIcon"
				:name="leadingIcon"
				:size="iconSize"
			/>
		</template>
		<template #body>
			<span
				v-if="label"
				class="kk-button__label"
				:class="{
					'kk-button__label--text-underline': hasTextUnderline,
				}"
			>{{ label }}</span>
		</template>
		<template #trailing>
			<KkIcon
				v-if="trailingIcon"
				:name="trailingIcon"
				:size="iconSize"
			/>
		</template>
		<template #spinner>
			<KkIcon
				class="kk-button__spinner"
				:class="{
					'kk-button__spinner--loading': isLoading
				}"
				name="ic_loading_line_semibold"
				:size="spinnerSize"
			/>
		</template>
	</KkButtonBase>
</template>

<script>
export default {
	name: 'KkButton',
}
const KK_BUTTON_ICON_SIZE_MAP = {
	'lg': {
		icon: 'sm',
		spinner: 'md',
	},
	'md': {
		icon: 'sm',
		spinner: 'md',
	},
	'sm': {
		icon: 'xs',
		spinner: 'sm',
	},
	'xs': {
		icon: 'xs',
		spinner: 'sm',
	},
};
export const KK_BUTTON_SIZE_LIST = Object.keys(KK_BUTTON_ICON_SIZE_MAP);
export const KK_BUTTON_TYPE_LIST = [
	'', // for custom style
	'accent',
	'primary',
	'primary-subtle',
	'critical',
	'critical-subtle',
	'secondary',
	'text-primary',
	'text-secondary',
	'white',
	'semi-transparent',
];
export const KK_BUTTON_PROPS = {
	label: {
		type: String,
		default: '',
	},
	leadingIcon: {
		type: String,
		default: '',
	},
	trailingIcon: {
		type: String,
		default: '',
	},
	size: {
		type: String,
		default: 'md',
		validator: size => KK_BUTTON_SIZE_LIST.includes(size),
	},
	type: {
		type: String,
		default: 'primary',
		validator: type => KK_BUTTON_TYPE_LIST.includes(type),
	},
	isFullWidth: {
		type: Boolean,
		default: false,
	},
	isDisabled: {
		type: Boolean,
		default: false,
	},
	isLoading: {
		type: Boolean,
		default: false,
	},
	hasTextUnderline: {
		type: Boolean,
		default: false,
	},
	hasNoPadding: {
		type: Boolean,
		default: false,
	},
	isClickBypassLimit: {
		type: Boolean,
		default: false,
	},
};
</script>

<script setup>
import {computed} from 'vue';
import KkButtonBase from './KkButtonBase.vue';
import KkIcon from '../../KkIcon';

const emits = defineEmits(['click']);
const props = defineProps(KK_BUTTON_PROPS);

const iconSize = computed(() => KK_BUTTON_ICON_SIZE_MAP[props.size].icon);
const spinnerSize = computed(() => KK_BUTTON_ICON_SIZE_MAP[props.size].spinner);

const isTextSecondaryNoIcons = computed(() => !props.leadingIcon && !props.trailingIcon && props.type === 'text-secondary');
const hasTextUnderline = computed(() => props.hasTextUnderline || isTextSecondaryNoIcons.value);

const isTextType = computed(() => props.type === 'text-primary' || props.type === 'text-secondary');
const hasNoPadding = computed(() => props.hasNoPadding || isTextType.value);

const click = () => {
	emits('click');
};
</script>
